import emit from '../../common/events';
import heading from '../../common/headings';
import { AlertSize } from '../alert/alert';
import { BaseComponent } from '../base-component/base-component';
import { Catalog } from '../../common/constants/catalog';
import { OfferTypeChecker } from '../../common/utils/offer-type-util';
import { ProductCardData } from '../product-card/product-card-display';
import { cardAlert, renderItemPrice, renderPrice } from '../../common/utils/product-card-util';
import { customElement, property, state } from 'lit/decorators.js';
import {
  dataLayerDetail,
  getSanitizedProductTitle,
  hasAuthorInfo,
  populatePdpLocalStorageObject,
  renderPdpSmallCardTemplate,
} from '../product-card/utils/product-card.util';
import { formattedJoin } from '../../common/utils/util';
import {
  CREATE_CUSTOM_CONTRIBUTOR_LABEL,
  CUSTOM_STORE_LIST_CARD_VARIATION,
  getCreateCustomImage,
  getCreateCustomOrderType,
  renderCreateCustomProductCardMetadata,
  renderCreateCustomProperty,
} from './utils/create-custom-product.util';
import { getProductTitleWithoutEdition } from '../../common/utils/product-util';
import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';

@customElement('mhecomm-create-custom-product-card-display')
export class CreateCustomProductCardDisplay extends BaseComponent {
  @property()
  headingLevel = 'p';

  @property({ type: Object })
  product?: ProductCardData;

  @property({ type: Number })
  productIndex = 0;

  @property()
  store?: string;

  @property()
  variation = 'small-card';

  @property({ type: Boolean })
  isCalledFromCartPreview = false;

  @state()
  private showFullTitle = false;

  private isCreateEbook = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.isCreateEbook = OfferTypeChecker.isCreateEbook(this.product?.offerType);
  }

  renderComp(): TemplateResult | typeof nothing {
    if (!this.product) {
      return nothing;
    }

    switch (this.variation) {
      case 'large-card':
        return this.largeCard(this.product, this.isCreateEbook);
      case 'small-card':
        return this.smallCard(this.product, this.isCreateEbook);
      case CUSTOM_STORE_LIST_CARD_VARIATION:
        return this.customStoreListCard(this.product, this.isCreateEbook);
      default:
        return nothing;
    }
  }

  /**
   * Displays the large product card (cart list) for Create/custom products.
   *
   * @param product
   * @param isCreateEbook
   */
  private largeCard(product: ProductCardData, isCreateEbook: boolean): TemplateResult | typeof nothing {
    return html`
      <hr class="mt-1" aria-hidden="true" />
      ${cardAlert('large-cart-error-message', product, AlertSize.small)}
      <div class="card mb-3 border-lg-0">
        <div class="row card-body p-lg-0">
          <div class="col-4 col-sm-3 col-md-2 col-lg-2 d-flex align-items-start justify-content-center">
            ${this.getImage(product, isCreateEbook)}
          </div>
          <div class="col-8 col-sm-9 col-md-5 col-xl-8">
            <h3 class="h5">
              <a
                id="${getSanitizedProductTitle(product.isbn13, product.title)}"
                href="${product.url}"
                @click=${(e: Event) => this.dataLayerProductDetail(e)}
                class="fs-large fw-semibold">
                ${product.title}
              </a>
            </h3>
            <div>
              ${renderCreateCustomProductCardMetadata({
                isCreateEbook,
                copyrightYear: product.copyRightYear,
                product,
                showProductType: true,
              })}
            </div>
            <div class="mt-2">
              ${product!.isVirtual === false
                ? html`<div>
                    <span class="badge bg-light text-dark">This product will be shipped to you.</span>
                  </div>`
                : html``}
            </div>
          </div>
          <div class="col-12 col-md-5 col-xl-2">
            <hr class="hr-primary d-md-none" aria-hidden="true" />
            <div class="row justify-content-end">
              <div class="col-12 col-sm-12 col-md-12 col-lg-12">
                <div class="row pb-lg-1">
                  ${renderPrice(product, product.isbn13)} ${renderItemPrice(product, product.isbn13)}
                </div>
              </div>
              <div class="col-6 col-sm-12">
                <mhecomm-add-to-cart
                  ?showRemoveLink=${true}
                  ?showQuantity=${!product.isVirtual}
                  ?isVirtual=${product.isVirtual}
                  quantity=${product.quantity!}
                  businessUnit=${product.businessUnit}
                  store=${ifDefined(this.store)}
                  productTitle=${product.title}
                  isbn=${product.isbn || product.mhid!}
                  isbn13=${product.isbn13!}
                  ?showSchoolPricing=${false}
                  format=${ifDefined(product?.format)}
                  .programSourceId=${product?.programSourceId}
                  offerPricingId=${product?.offerPricingId ?? ''}
                  offerType=${product?.offerType ?? ''}
                  .productIndex=${this.productIndex! + 1 || 1}
                  .durationType=${product?.durationType}
                  .edition=${product?.edition}
                  .isEvergreenProduct=${product?.isEvergreen}>
                </mhecomm-add-to-cart>
              </div>
            </div>
          </div>
          <div class="row">
            <mhecomm-my-bookshelf-redeem-link .product="${this.product}" ?isCreateCustomProduct="${true}">
            </mhecomm-my-bookshelf-redeem-link>
          </div>
          <div class="row mt-4">
            <mhecomm-create-custom-product-supplements
              .supplements="${product.supplements}"
              ?showDivider=${true}
              headerLevel="h4">
            </mhecomm-create-custom-product-supplements>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Displays the small product card (checkout summary) for Create/custom products.
   *
   * @param product
   * @param isCreateEbook
   */
  private smallCard(product: ProductCardData, isCreateEbook: boolean): TemplateResult | typeof nothing {
    return this.isCalledFromCartPreview ? this.pdpSmallCard(product, isCreateEbook) : this.cartSmallCard(product, isCreateEbook);
  }

  cartSmallCard(product: ProductCardData, isCreateEbook: boolean) {
    product.title = getProductTitleWithoutEdition({
      title: product.title,
      programSourceId: product.programSourceId!,
      businessUnit: product.businessUnit,
      isVirtual: !!product.isVirtual,
      formatTypeDesc: product.formatTypeDesc!,
      duration: product.duration!,
      durationPeriod: product.durationPeriod!,
      ...(product?.offerType && { offerType: product?.offerType }),
    });

    return html`
      ${cardAlert('small-cart-error-message', product, AlertSize.small)}
      <div class="d-flex row gx-2">
        <div class="col-3 mb-3">${this.getImage(product, isCreateEbook)}</div>
        <div class="col-9">${heading(this.headingLevel, product.title, ['fs-6', 'fw-semibold', 'mb-1'])}</div>
        <div class="col-12 d-flex flex-column justify-content-between ps-1">
          <div class="d-flex justify-content-between">
            <span>Price</span>
            <span class="fw-bold fs-large">$${product.totalPrice?.toFixed(2)}</span>
          </div>
        </div>
        ${this.product?.supplements && this.product.supplements.length > 0
          ? html` <div class="col-12 d-flex flex-column mt-3 ps-1">
              <mhecomm-create-custom-product-supplements .supplements="${product.supplements}" headerLevel="p">
              </mhecomm-create-custom-product-supplements>
            </div>`
          : nothing}
      </div>
    `;
  }

  pdpSmallCard(product: ProductCardData, isCreateEbook: boolean) {
    return renderPdpSmallCardTemplate({
      product,
      isCreateEbook,
      headingLevel: this.headingLevel,
      showFullTitle: this.showFullTitle,
      store: this.store,
      productIndex: this.productIndex,
      onShowTitle: (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.showFullTitle = true;
        emit({
          element: this,
          name: 'show-title',
          detail: dataLayerDetail('Show Title', this.product),
        });
      },
    });
  }

  /**
   * Displays the product card for Create/custom products in the custom store list view.
   *
   * @param product
   * @param isCreateEbook
   */
  private customStoreListCard(product: ProductCardData, isCreateEbook: boolean): TemplateResult {
    return html`
      <div class="card shadow rounded border-0 mb-3">
        <div class="row card-body">
          <div class="col-4 col-sm-3 col-md-2 col-lg-2 d-flex align-items-start justify-content-center">
            <a tabindex="-1" href="${product.url}"> ${this.getImage(product, isCreateEbook, true)} </a>
          </div>
          <div class="col-8 col-sm-9 col-lg-7">
            <h2 class="h5">
              <a
                id="${getSanitizedProductTitle(product.isbn13)}"
                href="${product.url}"
                @click=${(e: Event) => this.dataLayerProductDetail(e)}
                class="fs-large fw-semibold">
                ${product.title}
              </a>
            </h2>
            ${renderCreateCustomProperty(product, 'isbn13', 'ISBN', ':')}
            ${hasAuthorInfo(product)
              ? html`<div><strong>${CREATE_CUSTOM_CONTRIBUTOR_LABEL}:</strong> ${formattedJoin(product.contributors!)}</div>`
              : nothing}
            ${renderCreateCustomProperty(product, 'format', 'Type', ':')}
            ${product.copyRightYear ? html`<div><span>&#169;</span>${product.copyRightYear}</div>` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Displays the product image.
   * The image is retrieved differently for Create ebooks than for other products.
   *
   * @param product
   * @param isCreateEbook
   * @param isClickable
   */
  private getImage(product: ProductCardData, isCreateEbook: boolean, isClickable = false): TemplateResult {
    const image = getCreateCustomImage({
      altText: product.title,
      createCoverImages: product.createCoverImages,
      identifier: product.isbn13 || '',
      image: product.image,
      isCreateEbook: isCreateEbook,
    });

    if (isClickable) {
      return html`
        <a tabindex="-1" href="${product.url}" @click=${(e: Event) => this.dataLayerProductDetail(e)}> ${image} </a>
      `;
    }

    return image;
  }

  /**
   * Updates the local storage and emits the event when clicking on the product link or image.
   *
   * @param event
   */
  private dataLayerProductDetail(event: Event): void {
    const product = this.product as ProductCardData;

    populatePdpLocalStorageObject(
      event as MouseEvent,
      this,
      this.productIndex!,
      product,
      this.store,
      Catalog.he,
      getCreateCustomOrderType(product)
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-custom-product-card-display': CreateCustomProductCardDisplay;
  }
}
